import { useEffect, useRef, useState } from "react";
import {
  GridCell,
  GridPresetTwoThirds,
  GridRow,
  HideFor,
  ListSearchPublicationArticle,
  LoadingOverlay,
  Pagination,
  PublicationSearchPageFilters,
  PublicationSearchPageMobileFilters,
  RadioButton,
  ScreenReaderOnlyText,
  SearchGlobalField,
  SortByRadioButton,
} from "/App.Components";
import {
  ContentSearchProps,
  DropdownHierarchyProps,
  DropdownItemProps,
  DropdownProps,
  InsightSearchBlockProps,
} from "/App.GeneratedTypes";
import useScrollTopTracker from "/Helpers/Hooks/useScrollTopTracker";
import { InsightSearchClient } from "/Helpers/InsightSearchClient";
import { DesignTheme } from "../../../App.GeneratedEnums";
import { createSpreadableBem } from "../../../Helpers/createBem";
import "./ContentSearch.scss";
import { RowSpacing } from "/Components/Layout/GridRow/GridRow";

const GetDropdownItem = (dropdown?: DropdownProps, value?: string) =>
  dropdown?.items?.find((i) => i.value === value);

const GetDropdownHierarchyItem = (
  dropdown?: DropdownHierarchyProps,
  value?: string
): DropdownItemProps | undefined => {
  let result = GetDropdownItem(dropdown, value);
  if (result) return result;
  for (let i = 0; i < (dropdown?.innerDropdowns?.length ?? 0); ++i) {
    result = GetDropdownItem(dropdown?.innerDropdowns[i], value);
    if (result) return result;
  }
};

interface Props extends ContentSearchProps {
  publicationSearch?: boolean;
}

const ContentSearch = ({ insightSearchBlock, publicationSearch }: Props) => {
  const client = new InsightSearchClient(
    insightSearchBlock.endpoint,
    insightSearchBlock.blockEndpoint,
    insightSearchBlock.language,
    insightSearchBlock.searchPageId
  );

  const [results, setResults] = useState(insightSearchBlock.searchResults);
  const [sortByRelevance, setSortByRelevance] = useState(
    insightSearchBlock.initialSortByRelevance
  );
  const [loading, setLoading] = useState(false);
  const [isBlockLoading, setIsBlockLoading] = useState(false);

  const [pageNumber, setPageNumber] = useState(
    insightSearchBlock.searchResults?.pageNumber ?? 1
  );
  const [searchTerm, setSearchTerm] = useState(
    insightSearchBlock.initialSearchTerm ?? ""
  );
  const [filterButtonsFilter, setFilterButtonsFilter] = useState(
    insightSearchBlock.customFilter ?? ""
  );
  const [fuzzySearchState, setFuzzySearchState] = useState(
    insightSearchBlock.fuzzySearch
  );
  const [fuzzySearchLabel, setFuzzySearchLabel] = useState(
    insightSearchBlock.fuzzySearchLabel
  );
  const [searchTipsLabel, setSearchTipsLabel] = useState(
    insightSearchBlock.searchTipsLabel
  );
  const [searchTips, setSearchTips] = useState(insightSearchBlock.searchTips);
  const [noResultsText, setNoResultsText] = useState(
    insightSearchBlock.noResultsText
  );
  const [searchPlaceholder, setSearchPlaceholder] = useState(
    insightSearchBlock.searchPlaceholder
  );
  const [filtersHeading, setFiltersHeading] = useState(
    insightSearchBlock.filtersHeading
  );

  const [isArticle, setIsArticle] = useState(
    insightSearchBlock.isArticle || false
  );

  const [areaDropdown, setAreaDropdown] = useState(
    insightSearchBlock.areaDropdown
  );
  const [themeDropdown, setThemeDropdown] = useState(
    insightSearchBlock.themeDropdown
  );
  const [yearDropdown, setYearDropdown] = useState(
    insightSearchBlock.yearDropdown
  );
  const [typeDropdown, setTypeDropdown] = useState(
    insightSearchBlock.typeDropdown
  );

  const [filterSelectedAriaLiveSuffix, setFilterSelectedAriaLiveSuffix] =
    useState(insightSearchBlock.filterSelectedAriaLiveSuffix);

  const [selectedYear, setSelectedYear] = useState(
    GetDropdownItem(
      insightSearchBlock.yearDropdown,
      `${insightSearchBlock.initialReportPublishedYear}`
    )
  );
  const [selectedTheme, setSelectedTheme] = useState(
    GetDropdownHierarchyItem(
      insightSearchBlock.themeDropdown,
      insightSearchBlock.initialTheme
    )
  );
  const [selectedArea, setSelectedArea] = useState(
    GetDropdownHierarchyItem(
      insightSearchBlock.areaDropdown,
      insightSearchBlock.initialArea
    )
  );
  const [selectedType, setSelectedType] = useState(
    insightSearchBlock.typeDropdown
      ? GetDropdownHierarchyItem(
          insightSearchBlock.typeDropdown,
          insightSearchBlock.initialType
        )
      : undefined
  );

  const [doScroll, setDoScroll] = useState(false);
  const scrollToRef = useRef<HTMLDivElement>(null);
  const setVisibleResultCountRef = (element: HTMLDivElement | null) => {
    if (element?.getClientRects().length) {
      scrollToRef.current = element;
    }
  };
  const scrollTop = useScrollTopTracker(scrollToRef);

  // Scroll effect after loading
  useEffect(() => {
    if (!doScroll) return;
    setDoScroll(false);
    window.scrollTo({
      top: scrollTop,
      behavior: "smooth",
    });
  }, [loading]);

  useEffect(() => {
    if (isArticle || insightSearchBlock.initialTheme) {
      client.UpdateSearchUrl(
        searchTerm,
        pageNumber,
        sortByRelevance ?? insightSearchBlock.orderedByRelevanceByDefault,
        filterButtonsFilter,
        selectedType?.value,
        selectedArea?.value,
        insightSearchBlock.initialTheme,
        selectedYear?.value,
        fuzzySearchState,
        isArticle
      );
    }
  }, []);

  // Load search results
  useEffect(() => {
    if (!loading) return;

    client.UpdateSearchUrl(
      searchTerm,
      pageNumber,
      sortByRelevance ?? insightSearchBlock.orderedByRelevanceByDefault,
      filterButtonsFilter,
      selectedType?.value,
      selectedArea?.value,
      selectedTheme?.value,
      selectedYear?.value,
      fuzzySearchState,
      isArticle
    );

    client
      .GetItems(
        searchTerm,
        pageNumber,
        sortByRelevance,
        filterButtonsFilter,
        selectedType?.value,
        selectedArea?.value,
        selectedTheme?.value,
        insightSearchBlock.fixedPublicationType,
        isArticle ? undefined : selectedYear?.value,
        isArticle ? selectedYear?.value : undefined,
        fuzzySearchState,
        isArticle
      )
      .then((res) => {
        setResults(res);
        setPageNumber(res.pageNumber);
        setLoading(false);
      });
  }, [loading]);

  // Toggle article/publications search block with search results
  useEffect(() => {
    if (!isBlockLoading) return;

    client
      .GetSearchBlock(searchTerm, sortByRelevance, fuzzySearchState, isArticle)
      .then((res) => {
        setResults(res.searchResults);
        filters.resetFilters(res);
        setFuzzySearchLabel(res.fuzzySearchLabel);
        setSearchTipsLabel(res.searchTipsLabel);
        setSearchTips(res.searchTips);
        setNoResultsText(res.noResultsText);
        setSearchPlaceholder(res.searchPlaceholder);
        client.UpdateSearchUrl(
          searchTerm,
          res.searchResults?.pageNumber ?? 1,
          sortByRelevance ?? res.orderedByRelevanceByDefault,
          "",
          res.initialType,
          res.initialArea,
          res.initialTheme,
          undefined,
          fuzzySearchState,
          isArticle
        );
        setIsBlockLoading(false);
      });
  }, [isArticle]);

  const hasPages =
    typeof results !== "undefined" && (results?.totalPages ?? 0) > 1;

  const filters = {
    applyFilters: () => {
      setPageNumber(1);
      setLoading(true);
    },
    clearAllFiltersText: insightSearchBlock.clearAllFiltersButtonText ?? "",
    clearAllFilters: () => {
      setSelectedArea(undefined);
      setSelectedTheme(undefined);
      setSelectedYear(undefined);
      setSelectedType(undefined);
      setFilterButtonsFilter("");
      setPageNumber(1);
      setLoading(true);
    },
    resetFilters: (searchBlock: InsightSearchBlockProps) => {
      setAreaDropdown(searchBlock.areaDropdown);
      setThemeDropdown(searchBlock.themeDropdown);
      setYearDropdown(searchBlock.yearDropdown);
      setTypeDropdown(searchBlock.typeDropdown);
      setFilterSelectedAriaLiveSuffix(searchBlock.filterSelectedAriaLiveSuffix);
      setFiltersHeading(searchBlock.filtersHeading);
      setSelectedArea(undefined);
      setSelectedTheme(
        GetDropdownHierarchyItem(
          searchBlock.themeDropdown,
          searchBlock.initialTheme
        )
      );
      setSelectedYear(undefined);
      setSelectedType(undefined);
      setFilterButtonsFilter("");
      setPageNumber(1);
    },
    areaDropdown,
    themeDropdown,
    yearDropdown,
    typeDropdown,
    selectedArea,
    selectedTheme,
    selectedYear,
    selectedType: selectedType,
    setSelectedArea,
    setSelectedTheme,
    setSelectedYear,
    setSelectedType: setSelectedType,
    filterSelectedAriaLiveSuffix,
  };

  const bem = createSpreadableBem("content-search-norad");
  const articlesLabel =
    insightSearchBlock?.searchToggle?.articlesLabel ?? "Artikler";
  const publicationsLabel =
    insightSearchBlock?.searchToggle?.publicationsLabel ?? "Publikasjoner";
  const numberOfHits = (insightSearchBlock.searchResultFormatString ?? "").replace(
    "_",
    (results?.totalItems ?? 0) + ""
  );

  return (
    <div {...bem("", { "--publication-search": publicationSearch })}>
      <LoadingOverlay loading={loading || isBlockLoading} />
      <div
        {...bem("__search-and-filter-wrapper", {
          "--publication-search": publicationSearch,
        })}
      >
        <GridRow rowSpacingMobileUp="xxl" rowSpacing={RowSpacing.l}>
          {insightSearchBlock.blockHeading && (
            <div className="grid-col-12">
              <h2 {...bem("__block-heading")}>
                {insightSearchBlock.blockHeading}
              </h2>
            </div>
          )}
          {insightSearchBlock?.searchToggle ? (
            <>
              <GridCell
                spanColsMobile={6}
                spanColsTablet={6}
                spanColsDesktop={4}
                spanColsDesktopLarge={3}
              >
                <div {...bem("__type-toggle")}>
                  <button
                    {...bem(
                      "__type-toggle-button",
                      { "__type-toggle-button--active": isArticle },
                      { "--left": !isArticle }
                    )}
                    onClick={() => {
                      if (!isArticle) {
                        setIsArticle(true);
                        setIsBlockLoading(true);
                      }
                    }}
                    type="button"
                    aria-pressed={isArticle}
                  >
                    {articlesLabel}
                  </button>
                  <button
                    {...bem(
                      "__type-toggle-button",
                      { "__type-toggle-button--active": !isArticle },
                      { "--right": isArticle }
                    )}
                    onClick={() => {
                      if (isArticle) {
                        setIsArticle(false);
                        setIsBlockLoading(true);
                      }
                    }}
                    type="button"
                    aria-pressed={!isArticle}
                  >
                    {publicationsLabel}
                  </button>
                </div>
              </GridCell>
              <GridPresetTwoThirds startColsDesktop={5}>
                <SearchGlobalField
                  setSearchTerm={setSearchTerm}
                  searchTerm={searchTerm}
                  doSearch={() => {
                    setPageNumber(1);
                    setLoading(true);
                  }}
                  doAutocomplete={() => {}}
                  autocompleteItems={[]}
                  setAutocompleteItems={() => {}}
                  id="content-search-field"
                  searchButtonAriaLabel={
                    insightSearchBlock.searchButtonAriaLabel ?? ""
                  }
                  searchPlaceholder={searchPlaceholder ?? ""}
                  fuzzySearchLabel={fuzzySearchLabel}
                  searchTipsLabel={searchTipsLabel}
                  searchTips={searchTips}
                  fuzzySearch={fuzzySearchState}
                  setFuzzySearch={() => {
                    setFuzzySearchState((old) => !old);
                    setPageNumber(1);
                    setLoading(true);
                  }}
                  theme={DesignTheme.Insight}
                  noBottomPadding
                  noTopPadding
                />
              </GridPresetTwoThirds>
            </>
          ) : (
            <GridPresetTwoThirds startColsDesktop={5}>
              <div {...bem("__search-field")}>
                <SearchGlobalField
                  setSearchTerm={setSearchTerm}
                  searchTerm={searchTerm}
                  doSearch={() => {
                    setPageNumber(1);
                    setLoading(true);
                  }}
                  doAutocomplete={() => {}}
                  autocompleteItems={[]}
                  setAutocompleteItems={() => {}}
                  id="content-search-field"
                  searchButtonAriaLabel={
                    insightSearchBlock.searchButtonAriaLabel ?? ""
                  }
                  searchPlaceholder={searchPlaceholder ?? ""}
                  fuzzySearchLabel={fuzzySearchLabel}
                  searchTipsLabel={searchTipsLabel}
                  searchTips={searchTips}
                  fuzzySearch={fuzzySearchState}
                  setFuzzySearch={() => {
                    setFuzzySearchState((old) => !old);
                    setPageNumber(1);
                    setLoading(true);
                  }}
                  theme={DesignTheme.Insight}
                  noBottomPadding
                  noTopPadding
                />
              </div>
            </GridPresetTwoThirds>
          )}
        </GridRow>
      </div>
      <GridRow>
        <GridCell
          spanColsMobile={6}
          spanColsDesktop={4}
          spanColsDesktopLarge={3}
        >
          <HideFor mobile tablet>
            <aside>
              {filtersHeading && (
                <h3 {...bem("__filter-heading")}>{filtersHeading}</h3>
              )}
              <PublicationSearchPageFilters {...filters} />
            </aside>
          </HideFor>
          <HideFor desktop desktopMedium desktopLargeUp>
            <div
              {...bem("__number-of-hits")}
              aria-live="polite"
              ref={setVisibleResultCountRef}
            >
              {numberOfHits}
            </div>
          </HideFor>
          <div {...bem("__mobile-filters")}>
            <PublicationSearchPageMobileFilters
              heading={filtersHeading}
              theme={DesignTheme.Insight}
            >
              <PublicationSearchPageFilters {...filters} />
            </PublicationSearchPageMobileFilters>
          </div>
        </GridCell>
        <GridPresetTwoThirds startColsDesktop={5}>
          <div {...bem("__number-of-hits-and-sorting")}>
            <HideFor mobile tablet>
              <div
                {...bem("__number-of-hits")}
                aria-live="polite"
                ref={setVisibleResultCountRef}
              >
                {numberOfHits}
              </div>
            </HideFor>
            <div {...bem("__sort-by")}>
              <ScreenReaderOnlyText
                ariaLive="polite"
                text={
                  sortByRelevance
                    ? insightSearchBlock.orderByRelevanceAriaLiveText
                    : insightSearchBlock.orderByDateAriaLiveText
                }
              />
              <SortByRadioButton
                description={insightSearchBlock.sortByLabel}
                theme={DesignTheme.Insight}
              >
                <RadioButton
                  name="sortby"
                  label={insightSearchBlock.relevanceLabel ?? ""}
                  disabled={loading || isBlockLoading}
                  checked={sortByRelevance}
                  theme={DesignTheme.Insight}
                  onClick={() => {
                    if (sortByRelevance) return;
                    setSortByRelevance(true);
                    setPageNumber(1);
                    setLoading(true);
                  }}
                />
                <RadioButton
                  name="sortby"
                  label={insightSearchBlock.dateLabel ?? ""}
                  checked={!sortByRelevance}
                  disabled={loading || isBlockLoading}
                  theme={DesignTheme.Insight}
                  onClick={() => {
                    if (!sortByRelevance) return;
                    setSortByRelevance(false);
                    setPageNumber(1);
                    setLoading(true);
                  }}
                />
              </SortByRadioButton>
            </div>
          </div>
          <ListSearchPublicationArticle
            {...results?.list}
            ariaLabelSearchResults={insightSearchBlock.ariaLabelSearchResults}
            noResultsText={noResultsText}
            theme={DesignTheme.Insight}
          />
          {hasPages && (
            <div {...bem("__pagination-container")}>
              <Pagination
                {...insightSearchBlock.pagination}
                currentPage={pageNumber}
                setCurrentPage={(pageNumber) => {
                  setPageNumber(pageNumber);
                  setLoading(true);
                  setDoScroll(true);
                }}
                totalPages={results.totalPages}
              />
            </div>
          )}
        </GridPresetTwoThirds>
      </GridRow>
    </div>
  );
};

export default ContentSearch;
